/**
 * Freemail Domain List
 *
 * Used by the onboarding wizard to decide whether to auto-fill the organization
 * name from the user's email domain. NOT a blocklist — users with freemail
 * addresses can still sign up and use Spoofio normally.
 *
 * See docs/ubiquitous-language.md → "Freemail Domain".
 */

export const FREEMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // Google
  'gmail.com',
  'googlemail.com',
  // Microsoft
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'ymail.com',
  'rocketmail.com',
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
  // Proton
  'protonmail.com',
  'proton.me',
  'pm.me',
  // Other major providers
  'aol.com',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'fastmail.com',
  'tutanota.com',
  'tuta.com',
  'yandex.com',
  'hey.com',
])

/**
 * Extract the lowercased domain portion of an email address.
 * Returns null if the input is malformed or missing a domain.
 */
function extractDomain(email: string): string | null {
  const at = email.indexOf('@')
  if (at < 0 || at === email.length - 1) return null
  return email.slice(at + 1).trim().toLowerCase()
}

/**
 * Returns true if the given email address uses a known freemail provider.
 *
 * Edge cases:
 * - Empty string or malformed email (no @) → returns true (treated as freemail
 *   so the wizard prompts the user for an explicit organization name rather
 *   than auto-filling garbage).
 * - Mixed case is normalized before comparison.
 */
export function isFreemailDomain(email: string): boolean {
  const domain = extractDomain(email)
  if (!domain) return true
  return FREEMAIL_DOMAINS.has(domain)
}

/**
 * Suggest a display name for a new organization based on the user's email
 * address. Returns null for freemail domains (the wizard should prompt the
 * user instead).
 *
 * Examples:
 *   "jason@hillsdale.edu" → "Hillsdale"
 *   "user@acme-corp.com"  → "Acme-corp"
 *   "user@gmail.com"      → null
 *   ""                    → null
 */
export function extractOrgNameFromEmail(email: string): string | null {
  if (isFreemailDomain(email)) return null
  const domain = extractDomain(email)
  if (!domain) return null
  const label = domain.split('.')[0]
  if (!label) return null
  return label.charAt(0).toUpperCase() + label.slice(1)
}
